import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import { baseUrl } from "../../config/baseUrl";
import {
  fetchCategoryOptions,
  fetchPackages,
  getPackageLabel,
  getTargetLabel,
} from "./couponFormUtils";

export default function CouponAdd() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [isFetchingPackages, setIsFetchingPackages] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [formval, setFormval] = useState({
    coupon_Code: "",
    discount: "",
    valid_from: "",
    valid_till: "",
    category: "",
    forId: "",
    status: "true",
    packageScope: "all",
    packageIds: [],
  });

  const requiresTargetSelection = ["library", "mentor", "ebook"].includes(
    formval.category
  );

  useEffect(() => {
    const loadOptions = async () => {
      if (!formval.category || formval.category === "all") {
        setTypeOptions([]);
        setAllPackages([]);
        return;
      }

      if (requiresTargetSelection) {
        try {
          setIsFetchingOptions(true);
          const list = await fetchCategoryOptions(formval.category, token);
          setTypeOptions(list);
        } catch (error) {
          console.error(error);
          toast.error("Failed to load list for selected type");
          setTypeOptions([]);
        } finally {
          setIsFetchingOptions(false);
        }
      } else {
        setTypeOptions([]);
      }

      if (formval.category === "exam") {
        try {
          setIsFetchingPackages(true);
          const packages = await fetchPackages(token);
          setAllPackages(packages);
        } catch (error) {
          console.error(error);
          toast.error("Failed to load packages");
          setAllPackages([]);
        } finally {
          setIsFetchingPackages(false);
        }
      } else {
        setAllPackages([]);
      }
    };

    loadOptions();
  }, [formval.category, requiresTargetSelection, token]);

  const availablePackages = formval.category === "exam" ? allPackages : [];

  const packageOptions = availablePackages.map((pkg) => ({
    value: pkg._id,
    label: getPackageLabel(pkg),
  }));

  const selectedPackageOptions = packageOptions.filter((pkg) =>
    formval.packageIds.includes(pkg.value)
  );

  const changeHandler = (e) => {
    let { name, value } = e.target;

    if (name === "coupon_Code") {
      value = value.toUpperCase();
    }

    if (name === "category") {
      setFormval((prev) => ({
        ...prev,
        category: value,
        forId: "",
        packageScope: "all",
        packageIds: [],
      }));
      return;
    }

    if (name === "forId") {
      setFormval((prev) => ({
        ...prev,
        forId: value,
        packageScope: "all",
        packageIds: [],
      }));
      return;
    }

    if (name === "packageScope") {
      setFormval((prev) => ({
        ...prev,
        packageScope: value,
        packageIds: value === "all" ? [] : prev.packageIds,
      }));
      return;
    }

    setFormval((prev) => ({ ...prev, [name]: value }));
  };

  const handlePackageChange = (selectedOptions) => {
    setFormval((prev) => ({
      ...prev,
      packageIds: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  const validateForm = () => {
    const {
      coupon_Code,
      discount,
      valid_from,
      valid_till,
      category,
      forId,
      packageScope,
      packageIds,
    } = formval;

    if (!coupon_Code.trim()) {
      toast.error("Coupon code is required");
      return false;
    }

    if (!discount || discount <= 0 || discount > 100) {
      toast.error("Discount must be between 1 and 100");
      return false;
    }

    if (!valid_from) {
      toast.error("Valid From date is required");
      return false;
    }

    if (!valid_till) {
      toast.error("Valid Till date is required");
      return false;
    }

    if (new Date(valid_till) <= new Date(valid_from)) {
      toast.error("Valid Till must be after Valid From");
      return false;
    }

    if (new Date(valid_from) < new Date().setHours(0, 0, 0, 0)) {
      toast.error("Valid From date cannot be in the past");
      return false;
    }

    if (requiresTargetSelection && !forId) {
      toast.error(`Please select a ${category} item`);
      return false;
    }

    if (category === "exam" && packageScope === "specific" && !packageIds.length) {
      toast.error("Please select at least one package");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      code: formval.coupon_Code.trim(),
      discountType: "percentage",
      discountValue: Number(formval.discount),
      validFrom: formval.valid_from,
      validTill: formval.valid_till,
      isActive: formval.status === "true",
      category: formval.category || "all",
      applyToAllPackages: formval.packageScope === "all",
      packageIds: formval.packageScope === "specific" ? formval.packageIds : [],
    };

    switch (formval.category) {
      case "library":
        payload.libraryId = formval.forId;
        break;
      case "mentor":
        payload.mentorId = formval.forId;
        break;
      case "ebook":
        payload.ebookId = formval.forId;
        break;
      default:
        break;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${baseUrl}/coupons/add`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      if (res.status === 200 || res.status === 201) {
        toast.success("Coupon added successfully");
        navigate("/coupons");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to add coupon");
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section className="main-sec">
      <div className="row align-items-center">
        <div className="col-lg-6">
          <div className="dashboard-title">
            <h4 className="dash-head">
              <i className="fa fa-plus me-2" />
              Add Coupon
            </h4>
          </div>
          <div className="custom-bredcump">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/coupons">Coupons</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Add Coupon
                </li>
              </ol>
            </nav>
          </div>
        </div>
        <div className="col-lg-6 d-flex justify-content-end">
          <Link to="/coupons" className="btn btn-info text-white">
            <i className="fa fa-arrow-left me-1" />
            Back
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="cards edit-usr">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Coupon Code<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="text"
                    name="coupon_Code"
                    className="form-control"
                    value={formval.coupon_Code}
                    onChange={changeHandler}
                    required
                    style={{ textTransform: "uppercase" }}
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Discount (%)<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="number"
                    name="discount"
                    className="form-control"
                    value={formval.discount}
                    onChange={changeHandler}
                    min="1"
                    max="100"
                    required
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Coupon Type<sup className="text-danger">*</sup>
                  </label>
                  <select
                    name="category"
                    className="form-control"
                    value={formval.category}
                    onChange={changeHandler}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="all">All</option>
                    <option value="library">Library</option>
                    <option value="mentor">Mentor</option>
                    <option value="ebook">Ebook</option>
                    <option value="exam">Package</option>
                  </select>
                </div>

                {requiresTargetSelection && (
                  <div className="col-lg-6 mb-4">
                    <label className="form-label">
                      Select {formval.category}
                      <sup className="text-danger">*</sup>
                    </label>
                    <select
                      name="forId"
                      className="form-control"
                      value={formval.forId}
                      onChange={changeHandler}
                      required
                      disabled={isFetchingOptions}
                    >
                      <option value="">
                        {isFetchingOptions ? "Loading..." : "Choose one"}
                      </option>
                      {typeOptions.map((item) => (
                        <option key={item._id} value={item._id}>
                          {getTargetLabel(item, formval.category)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formval.category === "exam" && (
                  <>
                    <div className="col-lg-6 mb-4">
                      <label className="form-label">Package Scope</label>
                      <select
                        name="packageScope"
                        className="form-control"
                        value={formval.packageScope}
                        onChange={changeHandler}
                      >
                        <option value="all">All Packages</option>
                        <option value="specific">Specific Packages</option>
                      </select>
                      <small className="text-muted d-block mt-1">
                        Specific Packages option me aap single ya multiple package select kar sakte ho.
                      </small>
                    </div>

                    {formval.packageScope === "specific" && (
                      <div className="col-lg-6 mb-4">
                        <label className="form-label">
                          Select Packages<sup className="text-danger">*</sup>
                        </label>
                        <Select
                          isMulti
                          options={packageOptions}
                          value={selectedPackageOptions}
                          onChange={handlePackageChange}
                          isLoading={isFetchingPackages}
                          placeholder={
                            isFetchingPackages
                              ? "Loading packages..."
                              : "Select one or more packages"
                          }
                          noOptionsMessage={() => "No packages found"}
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Valid From<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="date"
                    name="valid_from"
                    className="form-control"
                    value={formval.valid_from}
                    onChange={changeHandler}
                    min={today}
                    required
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Valid Till<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="date"
                    name="valid_till"
                    className="form-control"
                    value={formval.valid_till}
                    onChange={changeHandler}
                    min={formval.valid_from || today}
                    required
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    className="form-control"
                    value={formval.status}
                    onChange={changeHandler}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <div className="col-lg-12 text-center">
                  <button type="submit" className="btn-save" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-save me-1" />
                        Save Coupon
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>
        {`
          .btn-save {
            background: #008080;
            color: white;
            border: none;
            padding: 10px 30px;
            border-radius: 5px;
            font-weight: 500;
            transition: all 0.3s ease;
          }

          .btn-save:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
          }
        `}
      </style>
    </section>
  );
}
